import { useEffect, useRef, useState } from 'react';
import { Download, ExternalLink, Loader2, Scale, Tag, QrCode, PenLine, PackageCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useGenerateRmaLabel, useRmaRates, useUpdateRma } from '../hooks/useRma';
import { getRmaLabelUrl } from '../api';
import type { CpServiceCode, RmaOrder, RmaRateOption, RmaRatesResponse } from '../types';
import { CP_SERVICE_LABELS } from '../types';

interface Props {
  rma: RmaOrder;
}

const CP_TRACKING_URL =
  'https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=';

const CA_PROVINCES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
const POSTAL_RE = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;

const CARRIER_LABELS: Record<string, string> = {
  canada_post: 'Canada Post',
  planete_courrier: 'Planète Courrier',
};

const cad = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' });

function rateKey(o: RmaRateOption): string {
  return o.carrier === 'canada_post' ? `cp:${o.serviceCode}` : `pc:${o.serviceAgreementId}`;
}

type TFunc = ReturnType<typeof useTranslation>['t'];

// CP quotes carry transit as a bare day count ("1") or ISO duration ("P1D") —
// render either with a unit; anything else (a date, a phrase) passes through.
function formatTransitDays(v: string | null | undefined, t: TFunc): string | null {
  if (!v) return null;
  const days = v.match(/^P(\d+)D$/i)?.[1] ?? (/^\d+$/.test(v) ? v : null);
  if (days == null) return v;
  const n = Number(days);
  return t('rma.shipping.day', n === 1 ? '{{count}} day' : '{{count}} days', { count: n });
}

function rateEtaLine(o: RmaRateOption, t: TFunc): string | null {
  if (o.carrier === 'canada_post') {
    return formatTransitDays(o.expectedTransitTime, t) || o.expectedDeliveryDate || null;
  }
  if (o.deliveryEtaMessage) return o.deliveryEtaMessage;
  if (o.deliveryEta) {
    try {
      return new Date(o.deliveryEta).toLocaleString('en-CA', {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch { /* fall through */ }
  }
  return null;
}

function ratePrice(o: RmaRateOption): string {
  return o.priceTotal == null ? '—' : cad.format(o.priceTotal);
}

export function RmaShippingCard({ rma }: Props) {
  const { t } = useTranslation('operations');
  const [weight, setWeight] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [postal, setPostal] = useState('');

  // Rate-comparison state: shown options + the operator's (manual) selection.
  const [rates, setRates] = useState<RmaRatesResponse | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Error from the last compare — rendered INLINE (a toast expires in ~4s and
  // left a failed compare looking like a dead button; found live 2026-10-09).
  const [ratesError, setRatesError] = useState<string | null>(null);

  const generateLabel = useGenerateRmaLabel();
  const updateRma = useUpdateRma();
  const compareRates = useRmaRates();

  // Seed the From form once per RMA (keyed on id) so a query refetch after a
  // failed generate doesn't clobber the operator's in-progress edits.
  const seededId = useRef<string | null>(null);
  useEffect(() => {
    if (seededId.current === rma.id) return;
    seededId.current = rma.id;
    // Clamp to the ship_from_* column widths — the account name (varchar 255)
    // can exceed the 44-char name/company columns, and maxLength only limits
    // typing, not a value assigned here, so an unclamped seed would 22001 on PATCH.
    setName((rma.shipFromName ?? rma.accountName ?? '').slice(0, 44));
    setCompany((rma.shipFromCompany ?? rma.accountName ?? '').slice(0, 44));
    setLine1((rma.shipFromLine1 ?? '').slice(0, 44));
    setLine2((rma.shipFromLine2 ?? '').slice(0, 44));
    setCity((rma.shipFromCity ?? '').slice(0, 40));
    setProvince((rma.shipFromProvince ?? '').slice(0, 2));
    setPostal((rma.shipFromPostalCode ?? '').slice(0, 7));
  }, [rma.id, rma.shipFromName, rma.shipFromCompany, rma.shipFromLine1, rma.shipFromLine2,
    rma.shipFromCity, rma.shipFromProvince, rma.shipFromPostalCode, rma.accountName]);

  const provinceOk = CA_PROVINCES.includes(province.toUpperCase());
  const postalOk = POSTAL_RE.test(postal.trim());
  const canCompare = !!(name.trim() && line1.trim() && city.trim() && provinceOk && postalOk)
    && !generateLabel.isPending && !updateRma.isPending && !compareRates.isPending;

  const options = rates?.options ?? [];
  const selectedOption = options.find((o) => rateKey(o) === selectedKey) ?? null;

  // Any edit to the address/weight invalidates the quoted rates (the PC draft
  // behind them was built from the previous values — confirming it would ship
  // against stale data). Drop the panel; the operator re-compares.
  const invalidateRates = () => {
    if (rates) {
      setRates(null);
      setSelectedKey(null);
    }
    setRatesError(null);
  };

  const handleCompare = async () => {
    invalidateRates();
    try {
      await updateRma.mutateAsync({
        id: rma.id,
        data: {
          shipFromName: name.trim(),
          shipFromCompany: company.trim() || null,
          shipFromLine1: line1.trim(),
          shipFromLine2: line2.trim() || null,
          shipFromCity: city.trim(),
          shipFromProvince: province.trim().toUpperCase(),
          shipFromPostalCode: postal.trim().toUpperCase(),
        },
      });
    } catch {
      return; // useUpdateRma surfaces the error toast; don't rate a failed save
    }
    let result: RmaRatesResponse | null = null;
    try {
      result = await compareRates.mutateAsync({
        id: rma.id,
        weight: weight ? parseFloat(weight) : undefined,
      });
    } catch (err) {
      // ApiError.message IS the server body's readable error (e.g. Galaxie's
      // "Postal code … is only valid for MONTREAL…") — keep it on screen.
      setRatesError(err instanceof Error ? err.message : t('rma.shipping.compareFailed', 'Failed to compare rates'));
      return;
    }
    if (result) setRates(result);
  };

  const handleGenerate = async () => {
    if (!selectedOption || !rates) return;
    const weightNum = weight ? parseFloat(weight) : undefined;

    if (selectedOption.carrier === 'canada_post') {
      // Persist the chosen CP service, then mint the authorized return.
      try {
        await updateRma.mutateAsync({
          id: rma.id,
          // Domestic rating only ever returns the four DOM.* codes; the narrow
          // union just can't know that.
          data: { cpServiceCode: selectedOption.serviceCode as CpServiceCode },
        });
      } catch {
        return; // toast already shown; no label on a failed save
      }
      generateLabel.mutate({ id: rma.id, data: weightNum ? { weight: weightNum } : {} });
      return;
    }

    // Planète Courrier: confirm the priced draft returned by /rates.
    generateLabel.mutate({
      id: rma.id,
      data: {
        carrier: 'planete_courrier',
        draftId: rates.pcDraftId,
        serviceAgreementId: selectedOption.serviceAgreementId,
        serviceName: selectedOption.serviceName ?? undefined,
        priceTotal: selectedOption.priceTotal ?? undefined,
        ...(weightNum ? { weight: weightNum } : {}),
      },
    });
  };

  const busy = generateLabel.isPending || updateRma.isPending;

  // Draft: show editable From address + rate comparison + generate
  if (rma.status === 'draft') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">{t('rma.shipping.title', 'Shipping & Tracking')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('rma.shipping.introA', 'The parcel ships')}{' '}
            <strong>{t('rma.shipping.introFrom', 'from the customer')}</strong>{' '}
            {t('rma.shipping.introB', 'back to ATEK.')}{' '}
            {t('rma.shipping.introConfirm', "Confirm the customer's return (From) address, then compare carriers and generate the label.")}
          </p>

          <div className="space-y-2">
            <Label htmlFor="sf-name">{t('rma.shipping.contactName', 'Contact name')} <span className="text-destructive">*</span></Label>
            <Input
              id="sf-name" maxLength={44} required aria-required value={name}
              onChange={(e) => { setName(e.target.value); invalidateRates(); }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sf-company">{t('rma.shipping.company', 'Company')}</Label>
            <Input
              id="sf-company" maxLength={44} value={company}
              onChange={(e) => { setCompany(e.target.value); invalidateRates(); }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sf-line1">{t('rma.shipping.addressLine1', 'Address line 1')} <span className="text-destructive">*</span></Label>
            <Input
              id="sf-line1" maxLength={44} required aria-required value={line1}
              onChange={(e) => { setLine1(e.target.value); invalidateRates(); }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sf-line2">{t('rma.shipping.addressLine2', 'Address line 2')}</Label>
            <Input
              id="sf-line2" maxLength={44} value={line2}
              onChange={(e) => { setLine2(e.target.value); invalidateRates(); }}
            />
          </div>
          {/* 12-col: each validation error renders INSIDE its field's cell so it
              sits under the input it belongs to (12-col gives the province cell
              enough width for its message — 6-col left it a sliver). */}
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-5 space-y-2">
              <Label htmlFor="sf-city">{t('rma.shipping.city', 'City')} <span className="text-destructive">*</span></Label>
              <Input
                id="sf-city" maxLength={40} required aria-required value={city}
                onChange={(e) => { setCity(e.target.value); invalidateRates(); }}
              />
            </div>
            <div className="col-span-3 space-y-2">
              <Label htmlFor="sf-prov">{t('rma.shipping.province', 'Prov')} <span className="text-destructive">*</span></Label>
              <Input
                id="sf-prov"
                maxLength={2}
                required
                aria-required
                value={province}
                onChange={(e) => { setProvince(e.target.value.toUpperCase()); invalidateRates(); }}
                aria-invalid={province.length > 0 && !provinceOk}
                aria-describedby={province.length > 0 && !provinceOk ? 'sf-prov-err' : undefined}
              />
              {province.length > 0 && !provinceOk && (
                <p id="sf-prov-err" className="text-xs text-destructive">{t('rma.shipping.provinceError', 'Province must be a 2-letter code (e.g. QC, ON, BC).')}</p>
              )}
            </div>
            <div className="col-span-4 space-y-2">
              <Label htmlFor="sf-postal">{t('rma.shipping.postal', 'Postal')} <span className="text-destructive">*</span></Label>
              <Input
                id="sf-postal"
                maxLength={7}
                required
                aria-required
                value={postal}
                onChange={(e) => { setPostal(e.target.value.toUpperCase()); invalidateRates(); }}
                aria-invalid={postal.length > 0 && !postalOk}
                aria-describedby={postal.length > 0 && !postalOk ? 'sf-postal-err' : undefined}
              />
              {postal.length > 0 && !postalOk && (
                <p id="sf-postal-err" className="text-xs text-destructive">{t('rma.shipping.postalError', 'Postal code must look like A1A 1A1.')}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="weight">{t('rma.shipping.weight', 'Package weight (kg, optional)')}</Label>
            <Input
              id="weight"
              type="number"
              step="0.1"
              min="0"
              placeholder={t('rma.shipping.weightPlaceholder', 'e.g. 1.5')}
              value={weight}
              onChange={(e) => { setWeight(e.target.value); invalidateRates(); }}
            />
          </div>

          <Button
            onClick={handleCompare}
            variant="outline"
            disabled={!canCompare}
            className="w-full"
          >
            {busy || compareRates.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Scale className="h-4 w-4 mr-1" />
            )}
            {t('rma.shipping.compare', 'Compare Carriers & Rates')}
          </Button>

          {ratesError && (
            <p data-testid="rma-rates-error" role="alert" className="text-sm text-destructive">
              {ratesError}
            </p>
          )}

          {rates?.warnings?.canadaPost && (
            <p className="text-xs text-amber-600">{rates.warnings.canadaPost}</p>
          )}

          {rates && (
            <div className="space-y-2" data-testid="rma-rate-options">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {t('rma.shipping.optionsHeading', 'Return options — pick one ({{weight}} kg)', { weight: rates.weight })}
              </p>
              <ul className="space-y-2" role="radiogroup" aria-label={t('rma.shipping.optionsAriaLabel', 'Return carrier options')}>
                {options.map((o) => {
                  const key = rateKey(o);
                  const selected = key === selectedKey;
                  const eta = rateEtaLine(o, t);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setSelectedKey(key)}
                        className={cn(
                          'w-full text-left rounded-md border p-3',
                          selected ? 'border-primary bg-primary/5' : 'border-input',
                        )}
                      >
                        <div className="flex items-start gap-3">
                          {/* role="radio" needs a visible radio affordance — border
                              tint alone doesn't read as selectable. */}
                          <span
                            aria-hidden
                            className={cn(
                              'mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                              selected ? 'border-primary' : 'border-muted-foreground/50',
                            )}
                          >
                            {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                          </span>
                          <div className="min-w-0 flex-1 space-y-1.5">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-medium truncate">
                                {t(`rma.shipping.carrierName.${o.carrier}`, CARRIER_LABELS[o.carrier] ?? o.carrier)}
                                {(o.serviceName || o.serviceCode) && (
                                  <span className="text-muted-foreground font-normal">
                                    {' — '}{o.serviceName || o.serviceCode}
                                  </span>
                                )}
                              </div>
                              <div className="font-medium shrink-0">{ratePrice(o)}</div>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              {o.signatureRequired ? (
                                <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-xs">
                                  <PenLine className="h-3 w-3" />
                                  {t('rma.shipping.signature', 'Signature on delivery')}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 text-xs">
                                  <PackageCheck className="h-3 w-3" />
                                  {t('rma.shipping.noSignature', 'No signature — left in safe place')}
                                </span>
                              )}
                              {eta && <span className="text-muted-foreground truncate">{eta}</span>}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
                {options.length === 0 && (
                  <li className="text-sm text-muted-foreground text-center py-4">
                    {t('rma.shipping.noRates', 'No rates available.')}
                  </li>
                )}
              </ul>

              <Button onClick={handleGenerate} disabled={!selectedOption || busy} className="w-full">
                {busy ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Tag className="h-4 w-4 mr-1" />
                )}
                {t('rma.shipping.generate', 'Generate Label')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Label generated or later — carrier-aware display
  const isPc = rma.carrier === 'planete_courrier' || (!rma.carrier && rma.pcShipmentNumber);
  const trackingNumber = isPc ? rma.pcShipmentNumber : rma.cpTrackingPin;
  const serviceName = isPc
    ? (rma.pcServiceName ?? t('rma.shipping.carrierName.planete_courrier', 'Planète Courrier'))
    : (rma.cpServiceCode
      ? t(`rma.service.${rma.cpServiceCode}`, CP_SERVICE_LABELS[rma.cpServiceCode] ?? rma.cpServiceCode)
      : null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">{t('rma.shipping.title', 'Shipping & Tracking')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Carrier */}
        <div className="space-y-0.5">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {t('rma.shipping.carrierLabel', 'Carrier')}
          </p>
          <p className="text-sm">
            {t(`rma.shipping.carrierName.${rma.carrier ?? 'canada_post'}`, CARRIER_LABELS[rma.carrier ?? 'canada_post'])}
          </p>
        </div>

        {/* Tracking number */}
        {trackingNumber && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {isPc
                ? t('rma.shipping.pcTracking', 'Shipment / Tracking No.')
                : t('rma.shipping.cpTracking', 'Tracking PIN')}
            </p>
            <p className="font-mono text-sm font-semibold">{trackingNumber}</p>
            {!isPc && (
              <a
                href={`${CP_TRACKING_URL}${rma.cpTrackingPin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {t('rma.shipping.trackOnCp', 'Track on Canada Post')}
              </a>
            )}
          </div>
        )}

        {/* Service */}
        {serviceName && (
          <div className="space-y-0.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('rma.shipping.serviceLabel', 'Service')}
            </p>
            <p className="text-sm">{serviceName}</p>
            {isPc && rma.pcPriceTotal != null && (
              <p className="text-xs text-muted-foreground">
                {cad.format(Number(rma.pcPriceTotal))} {t('rma.shipping.taxesIn', '(taxes in)')}
              </p>
            )}
          </div>
        )}

        {/* QR code indicator (Canada Post) */}
        {!isPc && rma.cpQrCode && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <QrCode className="h-4 w-4" />
            <span>{t('rma.shipping.qrAvailable', 'QR code available for drop-off')}</span>
          </div>
        )}

        {/* Download label */}
        {trackingNumber && (
          <a
            href={getRmaLabelUrl(rma.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Download className="h-4 w-4" />
            {t('rma.shipping.downloadLabel', 'Download Label PDF')}
          </a>
        )}
      </CardContent>
    </Card>
  );
}
